import React, { ReactNode } from 'react';
import styled from '@emotion/styled';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';
import { IconButton, TextField, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { MenuRoute, Routes } from '.';
import { ActionRow, OccupyFreeSpace } from '../primitives/ActionRow';
import { useTextFilter } from '../schemed/Filtering/useTextFilter';
import { useIntl } from 'react-intl';
import isHotkey from 'is-hotkey';

interface MenuGroupItem {
    key: string;
    label: ReactNode;
    component: React.ComponentType<any>;
    componentProps?: any;
}

export interface MenuGroup {
    key: string;
    icon?: ReactNode;
    label: ReactNode;
    labelText?: string;
    labelTextId?: string;
    items: MenuGroupItem[];
}

interface Props {
    groups: MenuGroup[];
    noCloseButton?: boolean;
    noSearchField?: boolean;
}

interface MenuDisplayProps extends Props {
    pathPrefix: string;
}

const Wrapper = styled.div`
    padding: 3rem 1.5rem;
`;

const MenuWrapper = styled.ul`
    padding: 0.5rem 0;
    gap: 50px 30px;
    display: grid;
    grid-template-columns: repeat(3, 1fr);

    ${props => props.theme.breakpoints.down('sm')} {
        grid-template-columns: 1fr;
    }

    & > li > div, & > li > a {
        display: flex;
        flex-flow: row;
        align-items: center;
        font-size: 1.6rem;

        & > svg {
            font-size: 1.2em;
            display: inline;
            padding-right: 0.5rem;
            color: ${props => props.theme.palette.primary.main};
        }
        
        & h3 {
            font-size: inherit;
            padding-bottom: 3px;
            border-bottom: 1px dotted ${props => props.theme.palette.primary.main};
        }
    }


    & ul {
        padding: 0.5rem 1.75rem;
    }

    & li {
        list-style: none;
    }

    & a {
       text-decoration: none;
       color: inherit;
    }

    & ul a {
       color: ${props => props.theme.palette.primary.main};
    }
`;

const groupItemLink = (g: MenuGroup, i: MenuGroupItem) => `${g.key}${g.items.length > 1 ? "/" : ""}${g.items.length > 1 ? i.key : ""}`;

export const Submenu = (props: MenuDisplayProps) => {
    const history = useHistory();
    const { formatMessage } = useIntl();

    const filter = useTextFilter<MenuGroup>(mg => `${mg.label} ${mg.labelText || ""} ${mg.labelTextId ? formatMessage({ id: mg.labelTextId }) : ""} ${(mg.items || []).map(item => item.label).join(" ")}`);

    const filteredItems = filter.filterData(props.groups);

    return (
        <Wrapper>
            {(!props.noCloseButton || !props.noSearchField) && <ActionRow>
                {!props.noSearchField && <TextField
                  value={filter.filter}
                  onChange={e => filter.setFilter(e.target.value)}
                  autoFocus
                  placeholder={formatMessage({ id: "searchField.label" })}
                  style={{ flex: "1 0 auto "}}
                  onKeyPress={e => {
                    if(isHotkey("enter", e) && filteredItems.length === 1) {
                      const g = filteredItems[0];
                      history.push(`${props.pathPrefix}/${groupItemLink(g, g.items[0])}`);
                    }
                  }}
                  />}
                {!!props.noSearchField && <OccupyFreeSpace />}
                {!props.noCloseButton && <IconButton onClick={() => history.goBack()}><Close /></IconButton>}
            </ActionRow>}
            <MenuWrapper>
                {filteredItems.map(g => (
                    <li key={g.key}>
                        {g.items.length > 1 ? <>
                            <div>{g.icon}<Typography variant="h3">{g.label}</Typography></div>
                            <ul>
                                {g.items.map(i => <li key={i.key}>
                                    <Link to={`${props.pathPrefix}/${groupItemLink(g, i)}`}>
                                        <Typography>{i.label}</Typography>
                                    </Link>
                                </li>)}
                            </ul>
                        </>
                        : <Link to={`${props.pathPrefix}/${groupItemLink(g, g.items[0])}`}>
                            {g.icon}<Typography variant="h3">{g.label}</Typography>
                        </Link>}
                    </li>
                ))}
            </MenuWrapper>
        </Wrapper>
    );
}

export const SubmenuContainer = (props: Props) => {
    let { path } = useRouteMatch();
    const routes = props.groups.reduce((r,g) => ([
        ...r,
        ...g.items.map(i => ({
            path: `${path}/${groupItemLink(g, i)}`,
            component: i.component,
            componentProps: i.componentProps,
        })),
    ]), [] as MenuRoute[]);

    return (
        <Routes
            routes={[
                ...routes,
                { path: "/", component: () => <Submenu pathPrefix={path} {...props} /> },
            ]}
        />
    )
}
